import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import { toast } from 'react-toastify';
import SectionCard from '../../../components/SectionCard';
import StatCard from '../../../components/StatCard';
import StatusChip from '../../../components/StatusChip';
import InvButton from '../../../components/InvButton';
import SplitPaymentCollector from '../../../components/SplitPaymentCollector';
import { InvSelect, InvTextField } from '../../../components/InvField';
import {
  listRefunds,
  actionRefund,
  settleRefund,
} from '../../../api/pharmacyDispense';
import { getPharmacyActorId } from '../../../hooks/usePharmacyRole';
import useInvRole from '../../../hooks/useInvRole';
import { INV_ACTIONS } from '../../../utils/inventoryRoles';
import { formatINR, formatDateTime, parseMoney, formatQty } from '../../../utils';
import {
  mapList,
  mapPharmacyRefund,
  pharmacyRefundCnNo,
  pharmacyRefundRemaining,
  pharmacyRefundItemLabel,
  pharmacyRefundItemsSummary,
} from '../../../utils/pharmacyMappers';
import { IPD_COLORS, cardSx } from '../../../theme';
import { IPD_FIELD_HEIGHT } from '../../../../ipd/utils/ipdFieldStyles';
import { fadeInSx } from '../pharmacyStyles';
import { PharmacyFilterBar } from '../PharmacyUi';
import PharmacyTableState from '../PharmacyTableState';

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'SETTLED', label: 'Settled' },
  { value: '', label: 'All' },
];

const TABLE_HEADERS = ['CN no', 'Bill', 'Patient', 'Amount', 'Items', 'Reason', 'Raised at', 'Status', 'Actions'];

function formatBillRef(row) {
  const num = row.bill_number;
  if (num && num !== row.bill_id && !String(num).match(/^[0-9a-f-]{36}$/i)) {
    return num;
  }
  const id = row.bill_id || row.pharmacy_bill_id;
  if (!id) return '—';
  return String(id).length > 12 ? `…${String(id).slice(-8)}` : id;
}

function RefundItemsList({ row, dense }) {
  const items = row?.refund_items || row?.items || [];
  if (!items.length) {
    return (
      <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>No items</Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: dense ? 0.5 : 0.75 }}>
      {items.map((item, i) => (
        <Box
          key={item.bill_line_id || item.item_id || i}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            alignItems: 'flex-start',
            py: dense ? 0.35 : 0.6,
            px: dense ? 0 : 1,
            borderRadius: dense ? 0 : '6px',
            backgroundColor: dense ? 'transparent' : IPD_COLORS.white,
            borderBottom: dense && i < items.length - 1 ? `1px dashed ${IPD_COLORS.border}` : 'none',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: IPD_COLORS.textPrimary, lineHeight: 1.3 }}>
              {item.item_name || 'Item'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 0.15 }}>
              Qty {formatQty(item.return_quantity)}
              {item.batch_number ? ` · Batch ${item.batch_number}` : ''}
            </Typography>
          </Box>
          {item.refund_amount != null && item.refund_amount !== '' && (
            <Typography sx={{ fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
              {formatINR(item.refund_amount)}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

function RefundActionSummary({ row }) {
  if (!row) return null;
  return (
    <Box sx={{
      p: 1.5,
      borderRadius: '8px',
      backgroundColor: IPD_COLORS.panelHeaderBg,
      border: `1px solid ${IPD_COLORS.border}`,
      mb: 2,
    }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: IPD_COLORS.primary }}>
        {pharmacyRefundCnNo(row)}
      </Typography>
      <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 0.5 }}>
        Requested {formatINR(row.total_amount || row.amount)}
        {row.reason ? ` · ${row.reason}` : ''}
      </Typography>
      <Box sx={{
        mt: 1.25,
        pt: 1,
        borderTop: `1px solid ${IPD_COLORS.border}`,
      }}>
        <Typography sx={{
          fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
          textTransform: 'uppercase', color: IPD_COLORS.textMuted, mb: 0.75,
        }}>
          Medicines returned
        </Typography>
        <RefundItemsList row={row} />
      </Box>
    </Box>
  );
}

export default function RefundsQueueTab() {
  const [status, setStatus] = useState('PENDING');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [settleTarget, setSettleTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [modalApproveAmount, setModalApproveAmount] = useState('');
  const [modalRejectReason, setModalRejectReason] = useState('');
  const { can } = useInvRole();
  const canApprove = can(INV_ACTIONS.REFUND_APPROVE);
  const canReject = can(INV_ACTIONS.REFUND_REJECT);
  const canSettle = can(INV_ACTIONS.REFUND_SETTLE);
  const canAct = canApprove || canReject || canSettle;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listRefunds(status ? { status } : {});
      setRows(mapList(data, mapPharmacyRefund));
    } catch (err) {
      setError(err?.message || 'Failed to load refunds');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    function onFocus() { load(); }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const pendingCount = useMemo(
    () => rows.filter((r) => r.status === 'PENDING').length,
    [rows],
  );

  function openApprove(row) {
    if (!canApprove) {
      toast.error('You do not have permission to approve refunds');
      return;
    }
    const requested = parseMoney(row.total_amount || row.amount);
    setModalApproveAmount(String(requested));
    setApproveTarget(row);
  }

  function openReject(row) {
    if (!canReject) {
      toast.error('You do not have permission to reject refunds');
      return;
    }
    setModalRejectReason('');
    setRejectTarget(row);
  }

  function closeApprove() {
    if (busyId) return;
    setApproveTarget(null);
    setModalApproveAmount('');
  }

  function closeReject() {
    if (busyId) return;
    setRejectTarget(null);
    setModalRejectReason('');
  }

  async function submitApprove() {
    const row = approveTarget;
    if (!row) return;
    const id = row.id || row.refund_id;
    const billId = row.bill_id || row.pharmacy_bill_id;
    if (!billId) {
      toast.error('Refund is missing bill reference');
      return;
    }
    const requested = parseMoney(row.total_amount || row.amount);
    const approved = parseMoney(modalApproveAmount);
    if (approved <= 0 || approved > requested + 0.0001) {
      toast.error('Approved amount must be > 0 and ≤ requested');
      return;
    }
    setBusyId(id);
    try {
      await actionRefund(billId, id, {
        action: 'approve',
        actioned_by: getPharmacyActorId(),
        approved_amount: String(approved.toFixed(4)),
      });
      toast.success('Refund approved');
      setApproveTarget(null);
      setModalApproveAmount('');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  }

  async function submitReject() {
    const row = rejectTarget;
    if (!row) return;
    const id = row.id || row.refund_id;
    const billId = row.bill_id || row.pharmacy_bill_id;
    const reason = modalRejectReason.trim();
    if (!billId) {
      toast.error('Refund is missing bill reference');
      return;
    }
    if (!reason) {
      toast.error('Rejection reason is required');
      return;
    }
    setBusyId(id);
    try {
      await actionRefund(billId, id, {
        action: 'reject',
        actioned_by: getPharmacyActorId(),
        rejection_reason: reason,
      });
      toast.success('Refund rejected');
      setRejectTarget(null);
      setModalRejectReason('');
      await load();
    } catch (err) {
      toast.error(err?.message || 'Reject failed');
    } finally {
      setBusyId(null);
    }
  }

  async function handleSettle(modes) {
    if (!settleTarget) return;
    if (!canSettle) {
      toast.error('You do not have permission to settle refunds');
      return;
    }
    const id = settleTarget.id || settleTarget.refund_id;
    const billId = settleTarget.bill_id || settleTarget.pharmacy_bill_id;
    if (!billId) {
      toast.error('Refund is missing bill reference');
      return;
    }
    setBusyId(id);
    try {
      await settleRefund(billId, id, { modes });
      toast.success('Refund settled — stock return posted to ledger');
      setSettleTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.message || 'Settle failed');
    } finally {
      setBusyId(null);
    }
  }

  const approveBusy = approveTarget && busyId === (approveTarget.id || approveTarget.refund_id);
  const rejectBusy = rejectTarget && busyId === (rejectTarget.id || rejectTarget.refund_id);

  return (
    <Box sx={fadeInSx}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
        <StatCard label="In queue" value={rows.length} icon={AssignmentReturnIcon} tone="teal" loading={loading} />
        <StatCard label="Pending" value={pendingCount} icon={AssignmentReturnIcon} tone="amber" loading={loading} />
      </Box>

      <PharmacyFilterBar>
        <Box sx={{ width: 200 }}>
          <InvSelect
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={STATUS_OPTIONS}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 8, display: { xs: 'none', sm: 'block' } }} />
        <InvButton
          size="small"
          variant="outlined"
          tone="teal"
          onClick={load}
          loading={loading}
          sx={{
            height: IPD_FIELD_HEIGHT,
            flexShrink: 0,
            alignSelf: 'flex-end',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Refresh
        </InvButton>
      </PharmacyFilterBar>

      <SectionCard title="Refunds queue" subtitle="Cross-bill credit notes awaiting action">
        <PharmacyTableState
          loading={loading}
          error={error}
          empty={!loading && !error && rows.length === 0}
          emptyMessage="No refunds for this filter."
          onRetry={load}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                  {TABLE_HEADERS.map((h) => (
                    <th key={h} style={{
                      textAlign: h === 'Actions' ? 'right' : 'left',
                      padding: '10px 12px',
                      fontSize: 11,
                      color: IPD_COLORS.textSecondary,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      whiteSpace: h === 'Actions' ? 'nowrap' : undefined,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const id = row.id || row.refund_id;
                  const remaining = pharmacyRefundRemaining(row);
                  const rowBusy = busyId === id;
                  return (
                    <tr key={id} style={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                      <td style={{ padding: '12px', fontWeight: 600, color: IPD_COLORS.primary }}>
                        {pharmacyRefundCnNo(row)}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Tooltip title={row.bill_id || row.pharmacy_bill_id || ''}>
                          <span>{formatBillRef(row)}</span>
                        </Tooltip>
                      </td>
                      <td style={{ padding: '12px' }}>{row.search_name || row.patient_name || '—'}</td>
                      <td style={{ padding: '12px', fontWeight: 600 }}>{formatINR(row.total_amount || row.amount)}</td>
                      <td style={{ padding: '12px', maxWidth: 220 }}>
                        <Tooltip title={(row.refund_items || []).map(pharmacyRefundItemLabel).join('\n') || '—'}>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.35 }} noWrap>
                            {pharmacyRefundItemsSummary(row)}
                          </Typography>
                        </Tooltip>
                      </td>
                      <td style={{ padding: '12px', maxWidth: 180 }}>
                        <Typography sx={{ fontSize: 12 }} noWrap>{row.reason || '—'}</Typography>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {formatDateTime(row.created_at || row.raised_at || row.payment_date)}
                      </td>
                      <td style={{ padding: '12px' }}><StatusChip status={row.status} /></td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        {(canApprove || canReject) && row.status === 'PENDING' && (
                          <Box sx={{ display: 'inline-flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {canApprove && (
                              <InvButton
                                size="small"
                                disabled={rowBusy}
                                loading={busyId === id && !!approveTarget}
                                onClick={() => openApprove(row)}
                              >
                                Approve
                              </InvButton>
                            )}
                            {canReject && (
                              <InvButton
                                size="small"
                                variant="outlined"
                                tone="teal"
                                disabled={rowBusy}
                                loading={busyId === id && !!rejectTarget}
                                onClick={() => openReject(row)}
                              >
                                Reject
                              </InvButton>
                            )}
                          </Box>
                        )}
                        {canSettle && row.status === 'APPROVED' && remaining > 0 && (
                          <InvButton size="small" disabled={rowBusy} onClick={() => setSettleTarget(row)}>
                            Settle
                          </InvButton>
                        )}
                        {(!canAct || (row.status !== 'PENDING' && !(row.status === 'APPROVED' && remaining > 0))) && (
                          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>
                            {canAct ? '—' : 'View only'}
                          </Typography>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </PharmacyTableState>
      </SectionCard>

      <Dialog
        open={!!approveTarget}
        onClose={closeApprove}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 18 }}>Approve refund</Typography>
          <IconButton size="small" onClick={closeApprove} disabled={approveBusy} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <RefundActionSummary row={approveTarget} />
          <InvTextField
            label="Approved amount (₹)"
            type="number"
            value={modalApproveAmount}
            disabled={approveBusy}
            helperText={
              approveTarget
                ? `Maximum ${formatINR(approveTarget.total_amount || approveTarget.amount)}`
                : ''
            }
            inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
            onChange={(e) => setModalApproveAmount(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <InvButton variant="outlined" tone="teal" onClick={closeApprove} disabled={approveBusy}>
            Cancel
          </InvButton>
          <InvButton loading={approveBusy} onClick={submitApprove}>
            Confirm approval
          </InvButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!rejectTarget}
        onClose={closeReject}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 18 }}>Reject refund</Typography>
          <IconButton size="small" onClick={closeReject} disabled={rejectBusy} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <RefundActionSummary row={rejectTarget} />
          <InvTextField
            label="Rejection reason *"
            value={modalRejectReason}
            disabled={rejectBusy}
            multiline
            minRows={2}
            placeholder="Explain why this refund cannot be approved"
            onChange={(e) => setModalRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <InvButton variant="outlined" tone="teal" onClick={closeReject} disabled={rejectBusy}>
            Cancel
          </InvButton>
          <InvButton
            loading={rejectBusy}
            onClick={submitReject}
            sx={{
              backgroundColor: IPD_COLORS.error.text,
              '&:hover': { backgroundColor: IPD_COLORS.error.text },
            }}
          >
            Confirm rejection
          </InvButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!settleTarget}
        onClose={() => !busyId && setSettleTarget(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography component="span" sx={{ fontWeight: 700, fontSize: 18 }}>
            Settle {settleTarget ? pharmacyRefundCnNo(settleTarget) : ''}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setSettleTarget(null)}
            disabled={!!busyId}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <RefundActionSummary row={settleTarget} />
          <SplitPaymentCollector
            totalDue={settleTarget ? pharmacyRefundRemaining(settleTarget) : 0}
            direction="payout"
            submitting={!!settleTarget && busyId === (settleTarget.id || settleTarget.refund_id)}
            disabled={!settleTarget}
            onSubmit={handleSettle}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
